import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import NoData from '../../../components/NoData';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import { Typography, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import SelectDropdown from '../../../components/SelectDropdown';
import { useOverTimeDataHooks } from '../hooks/useOverTimeDataHooks';
import {
  DEFAULT_LOCAL_SPEND_TOGGLE_YEAR,
  SPENDS_DATA,
  WEEKLY,
  WEEK_AVG,
  WEEK_AVG_TOGGLE_TEXT,
} from '../constants';
import { legendHighlightText } from '../service/SpendAndOppurtunitiesService';
import { ToggleComponent } from '../../../components/Toggle';
import { chartStyles } from '../styles/spendAndOppsStyles';

const OverTimeLineGraph = ({
  selectedStore,
  currentYear,
  totalYears,
  userStores,
  dataType,
  spendToggleState,
}) => {
  const [viewType, setViewType] = useState('Weekly');

  // Preparing yearsList for the dropdown
  const yearsList =
    totalYears &&
    Array.from({ length: totalYears }, (_, i) => ({
      label: `${currentYear - i}`,
      value: currentYear - i,
    }))?.filter((year) => !spendToggleState || year?.value >= DEFAULT_LOCAL_SPEND_TOGGLE_YEAR);

  // Hook call
  const {
    selectedYears,
    isLoading,
    error,
    overTimeSeries,
    currActualValues,
    currPlannedValues,
    overTimeLegend,
    xAxisOOTData,
    handleYearChange,
    handleOOTStoreChange,
    selectedComparisonStore,
    compareWith,
    displayWeekly,
    setDisplayWeekly,
    currActualValuesWeekly,
    currPlannedValuesWeekly,
  } = useOverTimeDataHooks(
    selectedStore,
    currentYear,
    viewType,
    userStores,
    dataType,
    spendToggleState
  );

  const handleViewTypeChange = (event, newViewType) => {
    if (newViewType) {
      setViewType(newViewType);
    }
  };

  const handleYearsChange = (event) => {
    handleYearChange(event);
  };

  const handleComparisonStoreChange = (newComparisonStore) => {
    handleOOTStoreChange(newComparisonStore);
  };

  return (
    <div id="OverTimeContainer" style={{ height: '550px', width: '100%', margin: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: '500', paddingBottom: '20px' }}>
          {dataType === SPENDS_DATA ? 'Spends' : 'Opportunities'} Over Time{' '}
          <Typography
            variant="body1"
            component="span"
            sx={{ fontSize: 14, fontStyle: 'italic', fontWeight: '300', verticalAlign: 'middle' }}
          >
            {viewType === WEEKLY && displayWeekly ? WEEK_AVG : ''}
          </Typography>
        </Typography>
        {viewType === 'Weekly' && (
          <div style={{ marginTop: '-15px' }}>
            <ToggleComponent
              setState={setDisplayWeekly}
              currentState={displayWeekly}
              toggleText={WEEK_AVG_TOGGLE_TEXT}
              disabled={isLoading}
            />
          </div>
        )}
      </div>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Box sx={{ display: 'flex', marginRight: 'auto' }}>
          <div>
            <Typography variant="body2">Years</Typography>
            <SelectDropdown
              isSelectAll={true}
              multiple={true}
              multiSelectDefaultValues={[yearsList[0]?.value]}
              options={yearsList}
              handleFilterChange={handleYearsChange}
              value={selectedYears}
              disabled={isLoading}
            />
          </div>
          <div>
            <Typography variant="body2">Compare With</Typography>
            <SelectDropdown
              options={compareWith}
              value={selectedComparisonStore}
              handleFilterChange={handleComparisonStoreChange}
              disabled={isLoading}
            />
          </div>
        </Box>
        <div>
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={handleViewTypeChange}
            aria-label="toggle Opportunites over Time line graph view"
            disabled={isLoading}
          >
            <ToggleButton
              value={'Weekly'}
              style={{
                background: viewType === 'Weekly' ? 'black' : isLoading ? '#e4e4e7' : '#fafafa',
                color: viewType === 'Weekly' ? 'white' : isLoading ? '#a9a9ab' : 'black',
                borderColor: isLoading ? '#a9a9ab' : 'black',
                textTransform: 'capitalize',
                height: '40px',
              }}
            >
              Weekly
            </ToggleButton>
            <ToggleButton
              value={'Monthly'}
              style={{
                background: viewType === 'Monthly' ? 'black' : isLoading ? '#e4e4e7' : '#fafafa',
                color: viewType === 'Monthly' ? 'white' : isLoading ? '#a9a9ab' : 'black',
                borderColor: isLoading ? '#a9a9ab' : 'black',
                textTransform: 'capitalize',
                height: '40px',
              }}
            >
              Monthly
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Box>
      {error ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
        >
          <NoData textValue="Error loading data" />
        </Box>
      ) : isLoading ? (
        <ShimmerComponent
          updatedStyle={{
            width: '100%',
            height: '70%',
            backgroundSize: '2000px 800px',
            border: '0px',
          }}
        >
          <BouncingLogo height="48vh" text="Loading..." />
        </ShimmerComponent>
      ) : overTimeSeries && overTimeSeries.length > 0 ? (
        <>
          <ReactEcharts
            style={{ height: '400px', width: '100%' }}
            option={{
              xAxis: {
                name: 'Time',
                type: 'category',
                data: xAxisOOTData,
                axisLabel: {
                  ...chartStyles.axisLabel,
                  interval: 0,
                  rotate: 45,
                },
                nameTextStyle: chartStyles.axisNameText,
                axisLine: {
                  lineStyle: chartStyles.axisLine,
                },
                axisTick: {
                  show: true,
                  lineStyle: chartStyles.axisLine,
                },
              },
              yAxis: [
                {
                  name: dataType === SPENDS_DATA ? 'Spends (USD)' : 'Opportunities',
                  type: 'value',
                  position: 'left',
                  axisLabel: {
                    ...chartStyles.axisLabel,
                    formatter: '{value}',
                  },
                  nameTextStyle: chartStyles.axisNameText,
                  axisTick: {
                    show: true,
                    lineStyle: chartStyles.axisLine,
                  },
                  axisLine: {
                    lineStyle: chartStyles.axisLine,
                  },
                  axisPointer: {
                    type: 'shadow',
                  },
                  data:
                    !displayWeekly && viewType === 'Weekly'
                      ? currActualValuesWeekly
                      : currActualValues,
                },
                {
                  type: 'value',
                  position: 'left',
                  axisLabel: {
                    ...chartStyles.axisLabel,
                    formatter: '{value}',
                  },
                  nameTextStyle: chartStyles.axisNameText,
                  axisTick: {
                    show: true,
                    lineStyle: chartStyles.axisLine,
                  },
                  axisLine: {
                    lineStyle: chartStyles.axisLine,
                  },
                  axisPointer: {
                    type: 'shadow',
                  },
                  data:
                    !displayWeekly && viewType === 'Weekly'
                      ? currPlannedValuesWeekly
                      : currPlannedValues,
                },
              ],
              tooltip: {
                enabled: true,
                trigger: 'axis',
              },
              legend: {
                type: 'scroll',
                data: [
                  ...overTimeLegend,
                  overTimeSeries &&
                    `${overTimeSeries?.find((store) => store.name === 'GALE Markets Average')?.name}`,
                  overTimeSeries &&
                    `${overTimeSeries?.find((store) => store.name === 'Regional Store Average')?.name}`,
                ],
                bottom: 0,
                textStyle: chartStyles.legendText,
              },
              series: overTimeSeries,
            }}
            opts={{
              renderer: 'canvas',
              devicePixelRatio: window?.devicePixelRatio || 2,
            }}
          />
          {legendHighlightText()}
        </>
      ) : (
        <NoData />
      )}
    </div>
  );
};

OverTimeLineGraph.propTypes = {
  selectedStore: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)])
    .isRequired,
  currentYear: PropTypes.number.isRequired,
  totalYears: PropTypes.number,
  userStores: PropTypes.arrayOf(
    PropTypes.shape({
      branch_number: PropTypes.number,
      branch_name: PropTypes.string,
      region_id: PropTypes.number,
      region_name: PropTypes.string,
    })
  ).isRequired,
  dataType: PropTypes.string.isRequired,
  spendToggleState: PropTypes.bool,
};

OverTimeLineGraph.defaultProps = {
  totalYears: 0,
  spendToggleState: false,
};

export default OverTimeLineGraph;
